import { useState, useEffect, useMemo } from 'react'
import { supabaseGetter } from '~/api/supabase-getter'
import { joinCropsWithSchedules } from '../utils/transform-aggregate-schedule'
import { useCropsContext } from '~/common/context/crops/crops-context'

/**
 * Fetches active schedules + phases for multiple crops using single aggregate query
 * Replaces the previous N+M parallel query approach with a single Supabase nested select
 *
 * @param {Object} params
 * @param {Array} params.crops - Array of crop objects with { id, organization_id, ... }
 * @returns {Object} { scheduleData, loading, error, invalidate }
 */
export const useMultiCropScheduleData = ({ crops, seasonId }) => {
  const [scheduleData, setScheduleData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { refreshKey } = useCropsContext()

  // Extract crop IDs from crops array for schedule query
  const cropIds = useMemo(
    () => (crops || []).map(c => c.id),
    [crops]
  )

  // Extract unique organization IDs from crops for query filtering
  const orgIds = useMemo(
    () => [...new Set((crops || []).map(c => c.organization_id).filter(Boolean))],
    [crops]
  )

  useEffect(() => {
    async function fetchSchedules() {
      if (!crops || crops.length === 0) {
        setScheduleData([])
        setLoading(false)
        setError(null)
        return
      }

      setLoading(true)
      setError(null)

      try {
        // Fetch schedules by crop IDs (no crop join - preserves upstream crop data)
        // When viewing a specific historical season, fetch that season's schedules instead of active ones
        const { data: schedulesData, error: fetchError } = seasonId
          ? await supabaseGetter.cropSchedules.getByCropIdsAndSeason(cropIds, seasonId)
          : await supabaseGetter.cropSchedules.getActiveByCropIds(cropIds)

        if (fetchError) throw fetchError

        // Client-side left join: Combine crops with schedules
        // This preserves all upstream crop data (organization, templates, families)
        const combined = joinCropsWithSchedules(crops, schedulesData)

        setScheduleData(combined)
      } catch (err) {
        console.error('Error fetching crop schedules:', err)
        setError(err.message)
        setScheduleData([])
      } finally {
        setLoading(false)
      }
    }

    fetchSchedules()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cropIds.join(','), orgIds.join(','), refreshKey, seasonId])  // Use stabilized dependencies + refreshKey for crop edits

  return {
    scheduleData,  // Array of { crop, schedule, phases }
    loading,
    error,
    invalidate: () => {
      supabaseGetter.cropSchedules.invalidate()
      supabaseGetter.cropPhases.invalidate()
      supabaseGetter.crops.invalidate()
    }
  }
}
